/**
 * courtage-api.ts — typed feature wrapper + German label maps + wire→view helpers
 * for the Courtage area (Story UI-U5). The E11 backend is fully built; these thin
 * reads (GET /commission/statements[:id]|akonto|splits|expectations + the existing
 * reconciliation Kennzahlen) finally make it visible.
 *
 * All mapping/formatting lives HERE (the invoices-api / V10-J precedent): components
 * render SERVER verdicts only — no client-side re-categorization, no diff math
 * (coding-standards #2). Finance-gated CHF fields arrive as `null` and render `—`.
 * The two human decisions ride the EXISTING audited E11 endpoints (override/transition,
 * camelCase bodies — module-local as-built shape; do NOT "fix" to snake_case).
 *
 * [Source: apps/web/src/features/invoices/api/invoices-api.ts] (wrapper + label-map pattern)
 * [Source: brokly-design-reference/lib/courtage-data.ts] (German vocabulary — ported, never imported)
 * [Source: docs/stories/ui-u5-courtage-tarifmatrix.md#FE — label maps + semantics]
 */

import { COMMISSION_EXPECTATION_STATUS_LABELS } from '@brokly/shared';
import { apiFetch } from '../../../lib/api-client';
import { chf } from '../../../lib/format';
import type { StatusTone } from '../../../components/ui/StatusDot';
import type { BadgeVariant } from '../../../components/ui/Badge';
import type {
  AkontoBalanceViewDto,
  AkontoStatusWire,
  CobrokerSplitStatus,
  CommissionDiffClassWire,
  CommissionExpectationStatus,
  CommissionExpectationViewDto,
  CommissionLineStatus,
  CommissionSplitViewDto,
  CommissionStatementDetailDto,
  CommissionStatementListItemDto,
  MatchLevel,
  ReconciliationKennzahlenDto,
} from '@brokly/shared';

// ── Access error (statements list 403 → honest Kein-Zugriff state) ─────────────

/** Thrown by fetchStatements on a 403 so the screen renders the Kein-Zugriff state. */
export class CourtageAccessError extends Error {
  constructor() {
    super('Kein Zugriff auf Courtage');
    this.name = 'CourtageAccessError';
  }
}

function isForbidden(e: unknown): boolean {
  const msg = e instanceof Error ? e.message.toLowerCase() : '';
  return msg.includes('forbidden') || msg.includes('403') || msg.includes('kein zugriff');
}

// ── Formatters (null → — ; never client-side math) ─────────────────────────────

/** CHF amount or an em-dash when Finance-gated to null. */
export function chfOrDash(v: string | null | undefined): string {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  return Number.isNaN(n) ? '-' : chf(n);
}

/** Signed absolute difference without the "CHF " prefix (mono columns); null → —. */
export function signedChfOrDash(v: string | null | undefined): string {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  if (Number.isNaN(n)) return '-';
  return `${n >= 0 ? '+' : '−'}${chf(Math.abs(n)).replace('CHF ', '')}`;
}

/** Signed percent (1 decimal); null → —. */
export function pctOrDash(v: string | null | undefined): string {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  if (Number.isNaN(n)) return '-';
  return `${n >= 0 ? '+' : '−'}${Math.abs(n).toFixed(1)} %`;
}

/** applied_rate numeric(8,6) string `0.010000` = 1 % → `× 1.00 %` (presentational only). */
export function rateDisplay(v: string | null | undefined): string {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  if (Number.isNaN(n)) return '-';
  return `× ${(n * 100).toFixed(2)} %`;
}

/** applied_factor numeric string → `× 1.00`; null → —. */
export function factorDisplay(v: string | null | undefined): string {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  if (Number.isNaN(n)) return '-';
  return `× ${n.toFixed(2)}`;
}

/** Differenz colour token for the signed amount: negative(warning)/positive(info)/~0(muted). */
export function diffToneClass(v: string | null | undefined): string {
  if (v === null || v === undefined) return 'text-muted-foreground';
  const n = Number(v);
  if (Number.isNaN(n) || Math.abs(n) < 0.5) return 'text-muted-foreground';
  return n < 0 ? 'text-status-warning' : 'text-status-info';
}

// ── Status / badge label maps (as-built enums → German + tone) ──────────────────

export interface ToneLabel {
  tone: StatusTone;
  label: string;
}

/** Statement header status → dot + text (open text — unknown → neutral + raw). */
export function statementStatus(status: string): ToneLabel {
  switch (status) {
    case 'received':
      return { tone: 'neutral', label: 'Eingegangen' };
    case 'header_ok':
      return { tone: 'info', label: 'In Prüfung' };
    case 'header_exception':
      return { tone: 'danger', label: 'Kopfprüfung fehlgeschlagen' };
    case 'duplicate':
      return { tone: 'warning', label: 'Dublette' };
    default:
      return { tone: 'neutral', label: status };
  }
}

export const LINE_STATUS_META: Record<CommissionLineStatus, ToneLabel> = {
  pending: { tone: 'warning', label: 'Offen' },
  matched: { tone: 'success', label: 'Abgeglichen' },
  deviation_open: { tone: 'warning', label: 'Differenz offen' },
  in_clarification: { tone: 'warning', label: 'In Klärung' },
  resolved: { tone: 'success', label: 'Erledigt' },
  written_off: { tone: 'neutral', label: 'Abgeschrieben' },
  reclaimed: { tone: 'info', label: 'Zurückgefordert' },
};

/** Differenzklasse soft-badge (category flag) + one-line Beschrieb tooltip. */
export const DIFF_CLASS_META: Record<CommissionDiffClassWire, { variant: BadgeVariant; label: string; beschrieb: string }> = {
  underpayment: {
    variant: 'warning',
    label: 'Unterzahlung',
    beschrieb: 'Der Versicherer hat weniger Courtage abgerechnet als vereinbart.',
  },
  overpayment: {
    variant: 'info',
    label: 'Überzahlung',
    beschrieb: 'Mehr Courtage abgerechnet als vereinbart: Rückbelastungsrisiko.',
  },
  nicht_berechtigt: {
    variant: 'danger',
    label: 'Nicht berechtigt',
    beschrieb: 'Kein gültiges Mandat oder keine aktive Police hinterlegt.',
  },
  fehlende_zeile: {
    variant: 'warning',
    label: 'Fehlende Zeile',
    beschrieb: 'Erwartete Courtage fehlt auf der Abrechnung vollständig.',
  },
  strukturbruch: {
    variant: 'danger',
    label: 'Strukturbruch',
    beschrieb: 'Unbekanntes Produkt oder keine freigegebene Tarifregel.',
  },
};

/** L-badge (escalation ladder — resting Neutral, never green). */
export function lBadge(level: MatchLevel | null): { variant: BadgeVariant; label: string } | null {
  if (!level) return null;
  const variant: BadgeVariant = level === 'L4' ? 'danger' : level === 'L3' ? 'warning' : 'neutral';
  return { variant, label: level };
}

export const AKONTO_STATUS_META: Record<AkontoStatusWire, ToneLabel> = {
  offen: { tone: 'neutral', label: 'Offen' },
  teilverrechnet: { tone: 'info', label: 'Teilverrechnet' },
  verrechnet: { tone: 'success', label: 'Verrechnet' },
  ueberfaellig: { tone: 'danger', label: 'Überfällig' },
};

export const SPLIT_STATUS_META: Record<CobrokerSplitStatus, ToneLabel> = {
  pending: { tone: 'neutral', label: 'Offen' },
  payout_due: { tone: 'warning', label: 'Auszahlung fällig' },
  paid: { tone: 'success', label: 'Ausbezahlt' },
  mismatch: { tone: 'danger', label: 'Abweichung' },
  reclaimed: { tone: 'info', label: 'Zurückgefordert' },
};

export const EXPECTATION_STATUS_META: Record<CommissionExpectationStatus, ToneLabel> = {
  expected: { tone: 'neutral', label: 'Erwartet' },
  received: { tone: 'success', label: 'Eingegangen' },
  disputed: { tone: 'warning', label: 'In Klärung' },
  overdue: { tone: 'danger', label: 'Überfällig' },
  // outside the courtage window (a termination ended it): kept, not outstanding
  zurueckgezogen: { tone: 'neutral', label: COMMISSION_EXPECTATION_STATUS_LABELS.zurueckgezogen },
};

/** Provisionsart ('06' = Akonto) short label for the line meta line. */
export function commissionKindLabel(kind: string | null): string {
  if (kind === '06') return 'Akonto';
  return kind ?? '-';
}

// ── Entscheid-bar legal-action derivation (display map of the as-built legality) ─

export interface LineTransitionAction {
  label: string;
  toStatus: CommissionLineStatus;
  tier: 'primary' | 'secondary' | 'menu';
}

export interface LineDecisionActions {
  terminal: boolean;
  hint?: string;
  transitions: LineTransitionAction[];
  /** The "Trotzdem freigeben" forced decision (override with a mandatory reason). */
  override?: { label: string; newStatus: CommissionLineStatus };
}

const TERMINAL_LINE_STATUSES: ReadonlySet<CommissionLineStatus> = new Set([
  'resolved',
  'written_off',
  'reclaimed',
]);

/**
 * Exactly the legal actions for a line's current status — a DISPLAY map of the
 * as-built LEGAL_COMMISSION_LINE_TRANSITIONS (the server re-validates every call).
 * `pending`/`matched` transitions that are pipeline work (matched/deviation_open)
 * are NOT offered manually.
 */
export function deriveLineActions(status: CommissionLineStatus): LineDecisionActions {
  if (TERMINAL_LINE_STATUSES.has(status)) {
    return { terminal: true, transitions: [] };
  }
  switch (status) {
    case 'deviation_open':
      return {
        terminal: false,
        transitions: [
          { label: 'In Klärung geben', toStatus: 'in_clarification', tier: 'primary' },
          { label: 'Erledigt', toStatus: 'resolved', tier: 'secondary' },
          { label: 'Abschreiben', toStatus: 'written_off', tier: 'menu' },
          { label: 'Rückfordern', toStatus: 'reclaimed', tier: 'menu' },
        ],
        override: { label: 'Trotzdem freigeben', newStatus: 'resolved' },
      };
    case 'in_clarification':
      return {
        terminal: false,
        transitions: [
          { label: 'Erledigt', toStatus: 'resolved', tier: 'primary' },
          { label: 'Abschreiben', toStatus: 'written_off', tier: 'menu' },
          { label: 'Rückfordern', toStatus: 'reclaimed', tier: 'menu' },
        ],
        override: { label: 'Trotzdem freigeben', newStatus: 'resolved' },
      };
    case 'matched':
      return {
        terminal: false,
        transitions: [
          { label: 'Als Differenz öffnen', toStatus: 'deviation_open', tier: 'secondary' },
          { label: 'Erledigt', toStatus: 'resolved', tier: 'primary' },
        ],
      };
    case 'pending':
    default:
      return {
        terminal: false,
        hint: 'Automatische Prüfung ausstehend',
        transitions: [],
      };
  }
}

// ── Period helper (Monitoring month Select → first/last day) ────────────────────

export interface PeriodBounds {
  period_from: string;
  period_to: string;
}

/** `YYYY-MM` → { first, last } day of that month (ISO date). */
export function monthBounds(month: string): PeriodBounds {
  const [y, m] = month.split('-').map((s) => parseInt(s, 10));
  const year = y ?? new Date().getFullYear();
  const mon = m ?? new Date().getMonth() + 1;
  const first = `${year}-${String(mon).padStart(2, '0')}-01`;
  const lastDay = new Date(year, mon, 0).getDate();
  const last = `${year}-${String(mon).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { period_from: first, period_to: last };
}

/** Current month as `YYYY-MM` (Monitoring default). */
export function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// ── Fetch wrappers ──────────────────────────────────────────────────────────────

export interface StatementListFilters {
  status?: string;
  carrier_uid?: string;
  period_from?: string;
  period_to?: string;
}

/** GET /commission/statements — 403 → CourtageAccessError (honest Kein-Zugriff). */
export async function fetchStatements(
  filters: StatementListFilters = {},
): Promise<CommissionStatementListItemDto[]> {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.carrier_uid) qs.set('carrier_uid', filters.carrier_uid);
  if (filters.period_from) qs.set('period_from', filters.period_from);
  if (filters.period_to) qs.set('period_to', filters.period_to);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  try {
    return await apiFetch<CommissionStatementListItemDto[]>(`/commission/statements${query}`);
  } catch (e) {
    if (isForbidden(e)) throw new CourtageAccessError();
    throw e;
  }
}

/** GET /commission/statements/:id — the peek detail (head + lines + Korrekturgruppen). */
export function fetchStatement(id: string): Promise<CommissionStatementDetailDto> {
  return apiFetch<CommissionStatementDetailDto>(`/commission/statements/${id}`);
}

/** GET /commission/akonto — the running Akonto-Salden. */
export function fetchAkonto(): Promise<AkontoBalanceViewDto[]> {
  return apiFetch<AkontoBalanceViewDto[]>('/commission/akonto');
}

/** GET /commission/splits — the co-broker split rows. */
export function fetchSplits(): Promise<CommissionSplitViewDto[]> {
  return apiFetch<CommissionSplitViewDto[]>('/commission/splits');
}

/** GET /commission/expectations — the Erwartungen for a period. */
export function fetchExpectations(period: PeriodBounds): Promise<CommissionExpectationViewDto[]> {
  const qs = new URLSearchParams({ period_from: period.period_from, period_to: period.period_to });
  return apiFetch<CommissionExpectationViewDto[]>(`/commission/expectations?${qs.toString()}`);
}

/** GET /commission/reconciliation/kennzahlen — Monitoring KPI tiles (existing endpoint). */
export function fetchKennzahlen(period: PeriodBounds): Promise<ReconciliationKennzahlenDto> {
  const qs = new URLSearchParams({ period_from: period.period_from, period_to: period.period_to });
  return apiFetch<ReconciliationKennzahlenDto>(`/commission/reconciliation/kennzahlen?${qs.toString()}`);
}

// ── Mutations (EXISTING audited E11 endpoints — camelCase as-built bodies) ──────

export interface TransitionLineBody {
  toStatus: CommissionLineStatus;
  reason?: string;
}

export interface OverrideLineBody {
  reason: string; // ≥ 10 chars — server rejects less
  newStatus?: CommissionLineStatus;
  newMatchLevel?: MatchLevel;
}

/** POST /admin/commission-exception/:lineId/transition — legal per-line status move. */
export function transitionLine(lineId: string, body: TransitionLineBody): Promise<unknown> {
  return apiFetch<unknown>(`/admin/commission-exception/${lineId}/transition`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** POST /admin/commission-exception/:lineId/override — forced decision + mandatory reason. */
export function overrideLine(lineId: string, body: OverrideLineBody): Promise<unknown> {
  return apiFetch<unknown>(`/admin/commission-exception/${lineId}/override`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Client-side mirror of the server's mandatory-reason rule (≥ 10 chars). */
export const MIN_OVERRIDE_REASON = 10;
